import { useEffect, useMemo, useRef, useState } from "react";
import invariant from "tiny-invariant";
import Modal from "react-bootstrap/Modal";

import { useAccessControl } from "../../app/access-control-context";
import {
  DefaultEditableStaffMemberContactDetail,
  useAvailableCenters,
  useUser,
  type DuplicateStaffNameCheckMatchedStaff,
  type StaffMember,
  type StaffMemberContactDetails
} from "../../hooks/use-user";
import { BaseModal } from "../modal/modal";
import { Confirm, ConfirmHandle } from "../confirm/confirm";
import { LoadingButton } from "../loading-button/loading-button";
import { LoadingOverlay } from "../loading-overlay/loading-overlay";
import { EditAccountForm, useEditAccountForm } from "./edit-account-form";
import { EditContactForm, useEditContactForm } from "./edit-contact-form";
import { SearchExistingUsers } from "./search-existing-users";
import { useFindUser } from "../../hooks/use-find-user";
import { FormHelpers } from "../forms";
import { ModalToastHeader } from "../../toasts/modal-toast-header";
import { useDebounce } from "../../hooks/use-debounce";

const onboardingSteps = [
  {
    name: "search",
    buttonLabels: { back: "Cancel", next: "Skip" },
    existingStaffButtonLabels: { back: "Cancel", next: "Next" }
  },
  {
    name: "contact",
    buttonLabels: { back: "Back", next: "Next" },
    existingStaffButtonLabels: { back: "Back", next: "Next" }
  },
  {
    name: "account",
    buttonLabels: { back: "Back", next: "Save" },
    existingStaffButtonLabels: { back: "Back", next: "Save" }
  }
] as const;

export const StaffAdministrationOnboardingWizard: React.FC<{
  show: boolean;
  onClose: () => void;
  onSuccessfulSave?: (staffMember: StaffMember) => void;
}> = ({ show, onClose, onSuccessfulSave }) => {
  const { activeCenterId } = useAccessControl();

  const [currentStep, setCurrentStep] = useState(() => {
    const step = onboardingSteps.find(({ name }) => name === "search");
    invariant(step, `No search step found.`);
    return step;
  });
  const currentStepIndex = onboardingSteps.indexOf(currentStep);

  const confirmCloseWithoutSavingModal = useRef<ConfirmHandle>(null);
  const [staffMemberData, setStaffMemberData] = useState<StaffMemberContactDetails | undefined>();
  const [duplicateNameMatch, setDuplicateNameMatch] = useState<DuplicateStaffNameCheckMatchedStaff | null>(null);

  const { newUserMutator, duplicateNameCheckMutator } = useUser(activeCenterId);
  const availableCentersQuery = useAvailableCenters(staffMemberData?.id);

  const goBack = () => {
    setCurrentStep(prevCurrentStep => onboardingSteps[onboardingSteps.indexOf(prevCurrentStep) - 1]);
  };

  const goNext = () => {
    setCurrentStep(prevCurrentStep => onboardingSteps[onboardingSteps.indexOf(prevCurrentStep) + 1]);
  };

  const editContactForm = useEditContactForm(staffMemberData);
  const onSubmitContactForm = editContactForm.handleSubmit(async contactValues => {
    if (staffMemberData?.id) {
      goNext();
      return;
    }

    try {
      const duplicateNameCheck = await duplicateNameCheckMutator.mutateAsync({
        firstName: contactValues.firstName,
        lastName: contactValues.lastName
      });

      if (duplicateNameCheck.match && duplicateNameCheck.staff) {
        setDuplicateNameMatch(duplicateNameCheck.staff);
        return;
      }

      goNext();
    } catch (error) {
      window.toasts.addModalToast({
        content: error instanceof Error ? error.message : "Unable to verify duplicate staff name. Please try again.",
        type: "danger"
      });
    }
  });

  const editAccountForm = useEditAccountForm();
  const onSubmitAccountForm = editAccountForm.handleSubmit(editAccountValues => {
    newUserMutator.mutate(
      {
        ...DefaultEditableStaffMemberContactDetail,
        ...staffMemberData,
        ...editContactForm.getValues(),
        ...editAccountValues,
        jobType: "FT"
      },
      {
        onSuccess: newUser => {
          onClose();
          onSuccessfulSave?.(newUser);
        }
      }
    );
  });

  const handleBack = () => {
    if (currentStepIndex === 0) {
      onClose();
    } else {
      goBack();
    }
  };

  const handleNext = () => {
    if (currentStep.name === "search" && availableCentersQuery.data?.length === 0 && staffMemberData?.id) {
      window.toasts.addModalToast({
        content: `${staffMemberData.displayName} already exists in all eligible centers`,
        type: "warning"
      });
      return;
    }

    if (currentStep.name === "search") {
      goNext();
    } else if (currentStep.name === "contact") {
      onSubmitContactForm();
    } else if (currentStep.name === "account") {
      onSubmitAccountForm();
    }
  };

  const localClose = () => {
    if (currentStep.name === "search") {
      onClose();
    } else {
      confirmCloseWithoutSavingModal.current?.confirm(() => {
        onClose();
      });
    }
  };

  useEffect(() => {
    if (show) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      setCurrentStep(onboardingSteps.find(({ name }) => name === "search")!);
      setSearchTerm("");
      setSelectedStaffId("");
      setDuplicateNameMatch(null);
      editAccountForm.reset();
      editContactForm.reset();
    }
  }, [show]);

  const modalTitle = useMemo(() => {
    if (currentStep.name === "contact") {
      return staffMemberData?.id ? "Edit Contact Information" : "Add Contact Information";
    } else if (currentStep.name === "account") {
      return "Add account information";
    } else {
      return "Search for an Existing User";
    }
  }, [currentStep.name, staffMemberData]);

  // hoisting state for re-render handling
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 350);

  const { findResults } = useFindUser(debouncedSearchTerm);

  useEffect(() => {
    if (selectedStaffId && findResults) {
      const existing = findResults.find(sm => sm.id === selectedStaffId);
      if (existing) {
        setStaffMemberData({
          ...existing,
          mobilePhone: FormHelpers.formatPhoneNumber(existing.mobilePhone),
          homePhone: FormHelpers.formatPhoneNumber(existing.homePhone),
          otherPhone: FormHelpers.formatPhoneNumber(existing.otherPhone),
          emergencyContactPhone: FormHelpers.formatPhoneNumber(existing.emergencyContactPhone)
        });
        return;
      }
    }

    // handles either empty id or not found
    setStaffMemberData({ ...DefaultEditableStaffMemberContactDetail });
  }, [selectedStaffId]);

  const closeDuplicateNameModal = () => {
    setDuplicateNameMatch(null);
  };

  const cancelDuplicateNameModal = () => {
    setDuplicateNameMatch(null);
    onClose();
  };

  const continueDuplicateNameModal = () => {
    setDuplicateNameMatch(null);
    goNext();
  };

  const duplicateStatusLabel = duplicateNameMatch?.status === "ACTIVE" ? "Active" : "Inactive";
  const duplicateNameMatchDisplayName = duplicateNameMatch
    ? `${duplicateNameMatch.firstName} ${duplicateNameMatch.lastName}`
    : "";

  return (
    <>
      <Modal
        show={show}
        className="onboarding-wizard-modal"
        backdrop="static"
        size="lg"
        fullscreen={"lg-down"}
        centered
        scrollable={true}
      >
        <Modal.Header closeButton onHide={localClose}>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="card">
          <LoadingOverlay
            loading={newUserMutator.isLoading || duplicateNameCheckMutator.isLoading || availableCentersQuery.isLoading}
          >
            <div className="onboarding-wizard-modal-body card-body">
              <ModalToastHeader />
              <SearchExistingUsers
                isCurrentStep={currentStep.name === "search"}
                searchTerm={searchTerm}
                onChangeSearchTerm={setSearchTerm}
                selectedStaffId={selectedStaffId}
                onChangeSelectedStaffId={setSelectedStaffId}
                findResults={findResults}
              />
              <EditContactForm editContactForm={editContactForm} isCurrentStep={currentStep.name === "contact"} />
              <EditAccountForm
                isCurrentStep={currentStep.name === "account"}
                availableCenters={availableCentersQuery.data ?? []}
                editAccountForm={editAccountForm}
              />
            </div>

            <Confirm
              ref={confirmCloseWithoutSavingModal}
              title="Confirm"
              message="Would you like to close the wizard and lose any entered data?"
            />
          </LoadingOverlay>
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <div className="w-25">
            <button className="btn btn-secondary w-100" onClick={handleBack}>
              {currentStep.buttonLabels.back}
            </button>
          </div>
          <div>
            Step {currentStepIndex + 1} of {onboardingSteps.length}
          </div>
          <div className="w-25">
            <LoadingButton
              isLoading={newUserMutator.isLoading || duplicateNameCheckMutator.isLoading}
              loadingMessage="Saving..."
              className="btn btn-primary w-100"
              onClick={handleNext}
            >
              {staffMemberData?.id ? currentStep.existingStaffButtonLabels.next : currentStep.buttonLabels.next}
            </LoadingButton>
          </div>
        </Modal.Footer>
      </Modal>

      <BaseModal
        show={Boolean(show && duplicateNameMatch)}
        onHide={closeDuplicateNameModal}
        title="DUPLICATE NAME FOUND"
        superZ
        body={
          duplicateNameMatch && (
            <div>
              <p>The first and last name you entered match an existing staff account.</p>
              <p>Please verify you are not adding a duplicate.</p>

              <hr />

              <p className="mb-0">
                <strong>Existing staff name:</strong> {duplicateNameMatchDisplayName}
                <br />
                <strong>Mobile Phone:</strong> {duplicateNameMatch.mobilePhone ?? "N/A"}
                <br />
                <strong>Personal Email:</strong> {duplicateNameMatch.personalEmail ?? "N/A"}
                <br />
                <br />
                <strong>Status:</strong> {duplicateStatusLabel}
              </p>

              <br />

              {duplicateNameMatch.status === "ACTIVE" ? (
                <p className="mb-0">
                  If this is a duplicate, please select Cancel to exit.
                  <br />
                  If this is not a duplicate, please Continue.
                </p>
              ) : (
                <p className="mb-0">
                  If this is a duplicate account, please select Cancel and then reactivate the original staff account.
                  <br />
                  If this is not a duplicate, please Continue.
                </p>
              )}
            </div>
          )
        }
        footer={
          <>
            <button className="btn btn-secondary" onClick={cancelDuplicateNameModal}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={continueDuplicateNameModal}>
              Continue
            </button>
          </>
        }
      />
    </>
  );
};
